// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { UndoOutlined } from '@ant-design/icons';
import { Button, InputBox } from '@imprivata-cloud/components';

import { Entitlement } from '@api/constants';
import type { ProxCardMaxNumberUpdateRequest } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { withRole } from '@common/authorization/Roles';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { ROLE_TYPE_ADMIN } from '../../constants';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';
import { useProxCardMaxNumberMutation, useProxCardMaxNumberQuery } from './useProxCardMaxNumber';

import './AdvancedSettings.less';

const TRANSLATION_ROOT = 'portal.advanced-settings';
const MIN_PROX_CARDS = 1;
const MAX_PROX_CARDS = 99;

export type MaxProxCardsError = 'required' | 'range' | null;

/**
 * Pure validation for the max-prox-cards value: required first, then integer in [1, 99].
 * Exported so the required/range branches can be unit-tested directly — the numeric-key
 * guard on the InputBox blocks non-integer characters, and Form's normalize converts the
 * string value to a number before the validator runs.
 */
export function getMaxProxCardsError(value: number | undefined | null): MaxProxCardsError {
  if (value === undefined || value === null) {
    return 'required';
  }
  if (!Number.isInteger(value) || value < MIN_PROX_CARDS || value > MAX_PROX_CARDS) {
    return 'range';
  }
  return null;
}

enum FieldNames {
  MAX_PROX_CARDS = 'maxProxCards',
}

interface FormValues {
  [FieldNames.MAX_PROX_CARDS]: number;
}

export function AdvancedSettingsForm() {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [messageApi, contextHolder] = message.useMessage();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [baseline, setBaseline] = useState<number | undefined>(undefined);

  useUnsavedChangesPrompt({ hasUnsavedChanges: isFormDirty });

  // While the form has unsaved edits, suspend background refetch so a window-focus
  // or reconnect-driven GET cannot overwrite the admin's in-progress input.
  const { data, isFetching, error: fetchError } = useProxCardMaxNumberQuery({
    refetchOnWindowFocus: !isFormDirty,
    refetchOnReconnect: !isFormDirty,
  });
  const { mutate, isPending } = useProxCardMaxNumberMutation();

  const isLoading = isFetching || isPending;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [FieldNames.MAX_PROX_CARDS]: data.proxCardMaxNumber });
      setBaseline(data.proxCardMaxNumber);
    }
  }, [data, form]);

  useEffect(() => {
    if (fetchError) {
      emitError(new AppError(fetchError));
    }
  }, [fetchError, emitError]);

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.key.length > 1 || /[0-9]/.test(e.key)) {
      return;
    }
    e.preventDefault();
  };

  const handleValuesChange = useCallback(() => {
    setIsFormDirty(true);
  }, []);

  const handleUndo = useCallback(() => {
    clearErrorNotifications();
    form.setFieldsValue({ [FieldNames.MAX_PROX_CARDS]: baseline });
    setIsFormDirty(false);
  }, [form, baseline, clearErrorNotifications]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      clearErrorNotifications();
      const proxCardMaxNumber = values[FieldNames.MAX_PROX_CARDS];
      const request: ProxCardMaxNumberUpdateRequest = { proxCardMaxNumber };
      mutate(request, {
        onSuccess: () => {
          void messageApi.success(t(`${TRANSLATION_ROOT}.notifications.save-success`));
          setIsFormDirty(false);
          setBaseline(proxCardMaxNumber);
        },
        onError: (e: ApiError) => {
          emitError(new AppError(e));
        },
      });
    },
    [mutate, messageApi, t, clearErrorNotifications, emitError],
  );

  const validateMaxProxCards = async (_: unknown, value: number | undefined) => {
    const error = getMaxProxCardsError(value);
    if (error === 'required') {
      return Promise.reject(t(`${TRANSLATION_ROOT}.fields.max-prox-cards.error-required`));
    }
    if (error === 'range') {
      return Promise.reject(t(`${TRANSLATION_ROOT}.fields.max-prox-cards.error-range`));
    }
    return Promise.resolve();
  };

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        id="advanced-settings-form"
        layout="vertical"
        colon={false}
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
        aria-label="advanced-settings-form"
      >
        <div className="advanced-settings-container">
          <div className="form-section">
            <div className="section-title">{t(`${TRANSLATION_ROOT}.prox-cards-header`)}</div>

            <Form.Item
              name={FieldNames.MAX_PROX_CARDS}
              extra={t(`${TRANSLATION_ROOT}.fields.max-prox-cards.helper-caveat`)}
              rules={[{ validator: validateMaxProxCards }]}
              validateTrigger={['onChange', 'onBlur']}
              normalize={(v: string) => (v === '' ? undefined : Number(v))}
            >
              <InputBox
                label={t(`${TRANSLATION_ROOT}.fields.max-prox-cards.label`)}
                disabled={isLoading}
                aria-label="max-prox-cards"
                className="prox-cards-input"
                onKeyDown={handleNumericKeyDown}
              />
            </Form.Item>
          </div>

          <div className="buttons-section">
            <Button
              type="text"
              className="text-button"
              onClick={handleUndo}
              disabled={isLoading || !isFormDirty}
              icon={<UndoOutlined className="undo-icon" />}
            >
              {t(`${TRANSLATION_ROOT}.buttons.undo`)}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              form="advanced-settings-form"
              disabled={isLoading || !isFormDirty}
            >
              {t(`${TRANSLATION_ROOT}.buttons.save`)}
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
}

export function AdvancedSettingsGate() {
  const { tenantInfo } = useSession();
  if (!hasEntitlement(tenantInfo, Entitlement.SSO)) {
    return null;
  }
  return <AdvancedSettingsForm />;
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(AdvancedSettingsGate);
